import { useState } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { Card, CardHeader, Button, Input, Alert, Spinner } from '../../components/UI';
import { useClients } from '../../hooks/useClients';
import { useAssessment } from '../../hooks/useAssessment';
import { Client, AccountConfig, SecurityAnalysis, S3Analysis, RDSRightsizingRecommendation, UpdateClientInput } from '../../types';
import {
  PlusIcon,
  PlayIcon,
  DocumentArrowDownIcon,
  LightBulbIcon,
  CloudArrowDownIcon,
  Cog6ToothIcon,
  ShieldExclamationIcon,
  CircleStackIcon,
  ServerIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { countAccountsMissingRole, accountHasRole, resolveClientAccounts } from '../../utils/clientAccounts';

type Route = '/' | '/callback' | '/reports' | '/network' | '/costs';

interface DashboardPageProps {
  onNavigate?: (route: Route) => void;
}

const formatCurrency = (value: number): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

import { CLOUDFORMATION_TEMPLATE, CLOUDFORMATION_TEMPLATE_MEMBER } from './cfn-templates';

// ─── Client Config Modal ──────────────────────────────────────────────────────

const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'sa-east-1', label: 'South America (São Paulo)' },
  { value: 'mx-central-1', label: 'Mexico (Central)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'eu-west-2', label: 'Europe (London)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
];

const ARN_REGEX = /^arn:aws:iam::\d{12}:role\/[\w+=,.@-]+$/;

interface ClientConfigModalProps {
  client: Client;
  onSave: (updates: UpdateClientInput) => Promise<void>;
  onClose: () => void;
}

function resolveInitialAccounts(client: Client): AccountConfig[] {
  if (client.accounts && client.accounts.length > 0) return client.accounts;
  if (client.roleArn) return [{ label: 'Principal', roleArn: client.roleArn, region: client.region || 'us-east-1' }];
  return [{ label: 'Principal', roleArn: '', region: 'us-east-1' }];
}

function ClientConfigModal({ client, onSave, onClose }: ClientConfigModalProps) {
  const [accounts, setAccounts] = useState<AccountConfig[]>(resolveInitialAccounts(client));
  const [scheduleEnabled, setScheduleEnabled] = useState(client.scheduleEnabled ?? false);
  const [notificationEmail, setNotificationEmail] = useState(client.notificationEmail || '');
  const [groupName, setGroupName] = useState(client.group || '');
  const [marginPct, setMarginPct] = useState(client.marginPercentage?.toString() || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateAccount = (index: number, field: keyof AccountConfig, value: unknown) => {
    setAccounts(prev => prev.map((acc, i) => i === index ? { ...acc, [field]: value } : acc));
    setError('');
  };

  const toggleRegion = (index: number, region: string) => {
    setAccounts(prev => prev.map((acc, i) => {
      if (i !== index) return acc;
      const current = acc.regions ?? [];
      const next = current.includes(region)
        ? current.filter(r => r !== region)
        : [...current, region];
      return { ...acc, regions: next };
    }));
  };

  const addAccount = () => {
    setAccounts(prev => [...prev, { label: `Cuenta ${prev.length + 1}`, roleArn: '', region: 'us-east-1' }]);
  };

  const removeAccount = (index: number) => {
    if (accounts.length <= 1) return;
    setAccounts(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    for (const acc of accounts) {
      if (!acc.label.trim()) { setError('Cada cuenta debe tener un nombre'); return; }
      if (acc.roleArn && !ARN_REGEX.test(acc.roleArn)) {
        setError(`ARN inválido en "${acc.label}". Formato: arn:aws:iam::123456789012:role/NombreRol`);
        return;
      }
    }
    const filledAccounts = accounts.filter(a => a.roleArn.trim());
    setSaving(true);
    try {
      await onSave({
        accounts: filledAccounts.length > 0 ? filledAccounts : undefined,
        roleArn: filledAccounts[0]?.roleArn || undefined,
        region: filledAccounts[0]?.region || undefined,
        group: groupName || undefined,
        marginPercentage: marginPct ? parseFloat(marginPct) : undefined,
        scheduleEnabled,
        notificationEmail: notificationEmail || undefined,
      });
      onClose();
    } catch {
      setError('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  // Regions grouped for the multi-select UI
  const REGION_GROUPS = [
    { group: 'América del Norte', regions: AWS_REGIONS.filter(r => ['us-east-1','us-east-2','us-west-1','us-west-2','ca-central-1','mx-central-1'].includes(r.value)) },
    { group: 'América del Sur',   regions: AWS_REGIONS.filter(r => ['sa-east-1'].includes(r.value)) },
    { group: 'Europa',            regions: AWS_REGIONS.filter(r => ['eu-west-1','eu-west-2','eu-central-1'].includes(r.value)) },
    { group: 'Asia Pacífico',     regions: AWS_REGIONS.filter(r => ['ap-south-1','ap-northeast-1','ap-southeast-1','ap-southeast-2'].includes(r.value)) },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-lg font-bold text-honne-navy">Configurar Cliente</h3>
          <p className="text-sm text-gray-500 mt-1">{client.name}</p>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {error && <Alert type="error" message={error} />}

          {/* Accounts section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-800">Cuentas AWS</p>
                <p className="text-xs text-gray-500 mt-0.5">Configura una o más cuentas para análisis</p>
              </div>
              <button
                onClick={addAccount}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-accent-500 text-accent-600 hover:bg-accent-50 transition-colors"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Agregar cuenta
              </button>
            </div>

            <div className="space-y-4">
              {accounts.map((acc, i) => (
                <div key={i} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  {/* Account header */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Cuenta {i + 1}
                    </span>
                    {accounts.length > 1 && (
                      <button onClick={() => removeAccount(i)} className="text-red-400 hover:text-red-600 transition-colors" title="Eliminar cuenta">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* MPA toggle */}
                  <div className="flex items-center justify-between p-2.5 mb-3 rounded-lg bg-blue-50 border border-blue-100">
                    <div>
                      <p className="text-xs font-semibold text-blue-800">Management Account (MPA)</p>
                      <p className="text-xs text-blue-600 mt-0.5">Auto-descubre todas las cuentas de la organización</p>
                    </div>
                    <button
                      onClick={() => updateAccount(i, 'isMpaAccount', !acc.isMpaAccount)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${acc.isMpaAccount ? 'bg-blue-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${acc.isMpaAccount ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Nombre / Etiqueta</label>
                      <input
                        value={acc.label}
                        onChange={e => updateAccount(i, 'label', e.target.value)}
                        placeholder={acc.isMpaAccount ? 'Ej: Organización Principal' : 'Ej: Producción, Staging'}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Región principal</label>
                      <select
                        value={acc.region}
                        onChange={e => updateAccount(i, 'region', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 bg-white"
                      >
                        {AWS_REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">ARN del Rol IAM</label>
                      <input
                        value={acc.roleArn}
                        onChange={e => updateAccount(i, 'roleArn', e.target.value)}
                        placeholder="arn:aws:iam::123456789012:role/HonneCostOptimizationRole"
                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 bg-white font-mono ${
                          accountHasRole(acc)
                            ? 'border-gray-300 focus:ring-accent-500 focus:border-accent-500'
                            : 'border-amber-300 bg-amber-50/40 focus:ring-amber-400 focus:border-amber-400'
                        }`}
                      />
                      {!accountHasRole(acc) && (
                        <p className="inline-flex items-center gap-1 text-xs text-amber-700 mt-1">
                          <ExclamationTriangleIcon className="h-3 w-3" />
                          Esta cuenta no tiene rol IAM configurado — no se incluirá en los análisis.
                        </p>
                      )}
                      {acc.isMpaAccount && (
                        <p className="text-xs text-blue-600 mt-1">
                          El rol debe tener permiso <code className="bg-blue-50 px-1 rounded">organizations:ListAccounts</code>. Se asumirá el mismo nombre de rol en cada cuenta miembro.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Excluded accounts — only shown when MPA is enabled */}
                  {acc.isMpaAccount && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-gray-600">
                          Cuentas excluidas
                          {(acc.excludedAccounts?.length ?? 0) > 0 && (
                            <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                              {acc.excludedAccounts!.length} excluidas
                            </span>
                          )}
                        </label>
                        <span className="text-xs text-gray-400">IDs de cuentas a omitir del análisis</span>
                      </div>
                      <div className="space-y-1.5">
                        {(acc.excludedAccounts ?? []).map((id, ei) => (
                          <div key={ei} className="flex items-center gap-2">
                            <input
                              value={id}
                              onChange={e => {
                                const newExcluded = [...(acc.excludedAccounts ?? [])];
                                newExcluded[ei] = e.target.value;
                                updateAccount(i, 'excludedAccounts', newExcluded);
                              }}
                              placeholder="123456789012"
                              className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-red-300 focus:border-red-400 bg-white"
                            />
                            <button
                              onClick={() => {
                                const newExcluded = (acc.excludedAccounts ?? []).filter((_, j) => j !== ei);
                                updateAccount(i, 'excludedAccounts', newExcluded);
                              }}
                              className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => updateAccount(i, 'excludedAccounts', [...(acc.excludedAccounts ?? []), ''])}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium mt-1"
                        >
                          <PlusIcon className="h-3 w-3" />
                          Excluir cuenta
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Ingresa el Account ID de 12 dígitos de cada cuenta a omitir.</p>
                    </div>
                  )}

                  {/* Multi-region selector */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600">
                        Regiones adicionales
                        {(acc.regions?.length ?? 0) > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold bg-accent-100 text-accent-700">
                            +{acc.regions!.length}
                          </span>
                        )}
                      </label>
                      <span className="text-xs text-gray-400">Selecciona todas las regiones donde tienes infraestructura</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 p-3 bg-white rounded-lg border border-gray-200 max-h-40 overflow-y-auto">
                      {REGION_GROUPS.map(group => (
                        <div key={group.group}>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 mt-1">{group.group}</p>
                          {group.regions.map(r => {
                            if (r.value === acc.region) return null; // primary region already included
                            const checked = acc.regions?.includes(r.value) ?? false;
                            return (
                              <label key={r.value} className="flex items-center gap-2 py-0.5 cursor-pointer group">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleRegion(i, r.value)}
                                  className="h-3.5 w-3.5 rounded border-gray-300 text-accent-500 focus:ring-accent-500"
                                />
                                <span className={`text-xs ${checked ? 'text-gray-800 font-medium' : 'text-gray-500'} group-hover:text-gray-800`}>
                                  {r.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">La región principal siempre se incluye. Marca las adicionales donde tengas recursos.</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Group/Sector */}
          <Input
            label="Grupo / Sector"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Ej: Sector Privado, Sector Educativo"
            helperText="Para organizar clientes en el dashboard"
          />

          {/* Margin */}
          <Input
            label="Margen de ganancia (%)"
            value={marginPct}
            onChange={(e) => setMarginPct(e.target.value)}
            placeholder="Ej: 15"
            helperText="Se aplica al costo en el reporte del cliente. En el dashboard interno se muestran ambos."
          />

          {/* Notification email */}
          <Input
            label="Email de notificación (opcional)"
            value={notificationEmail}
            onChange={(e) => setNotificationEmail(e.target.value)}
            placeholder="ops@empresa.com"
            helperText="Recibirá un email cuando el assessment automático termine"
          />

          {/* Schedule toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-700">Assessment automático</p>
              <p className="text-xs text-gray-500">Se ejecutará el día 10 de cada mes</p>
            </div>
            <button
              onClick={() => setScheduleEnabled(!scheduleEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${scheduleEnabled ? 'bg-accent-500' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${scheduleEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex gap-3 justify-end flex-shrink-0">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} loading={saving}>Guardar</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Security Section ─────────────────────────────────────────────────────────

function SecuritySection({ security }: { security: SecurityAnalysis }) {
  return (
    <Card>
      <CardHeader title={`Seguridad (${security.securityGroupFindings.length} grupos con puertos expuestos)`} />

      {/* Trusted Advisor / Security Hub status */}
      <div className="flex gap-3 mb-4">
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
          security.trustedAdvisorAvailable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {security.trustedAdvisorAvailable ? '✅' : '⚠️'} Trusted Advisor{' '}
          {security.trustedAdvisorAvailable ? 'activo' : 'no disponible (requiere Business/Enterprise Support)'}
        </span>
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
          security.securityHubAvailable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {security.securityHubAvailable ? '✅' : '⚠️'} Security Hub{' '}
          {security.securityHubAvailable ? 'activo' : 'no habilitado — se recomienda activarlo'}
        </span>
      </div>

      {security.securityGroupFindings.length === 0 ? (
        <p className="text-green-600 text-sm py-4 text-center">✅ No se encontraron puertos expuestos a 0.0.0.0/0</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-red-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-red-800 uppercase">Security Group</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-red-800 uppercase">VPC</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-red-800 uppercase">Puertos expuestos</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-red-800 uppercase">Riesgo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-red-800 uppercase">Recomendación</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {security.securityGroupFindings.map((sg) => (
                <tr key={sg.securityGroupId} className="hover:bg-red-50/30">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{sg.securityGroupName}</p>
                    <p className="text-xs text-gray-500">{sg.securityGroupId}</p>
                    {sg.isOrphaned && (
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#fef9c3', color: '#a16207' }}>
                        ⚠️ Sin recursos asociados
                      </span>
                    )}
                    {sg.associatedResources && sg.associatedResources.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {sg.associatedResources.slice(0, 3).map((r, i) => (
                          <span key={i} className="text-xs px-1.5 py-0.5 rounded font-mono" style={{ background: '#eff6ff', color: '#1d4ed8' }}>
                            {r.resourceType}: {r.resourceName || r.resourceId.substring(0, 15)}
                          </span>
                        ))}
                        {sg.associatedResources.length > 3 && (
                          <span className="text-xs text-gray-400">+{sg.associatedResources.length - 3} más</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{sg.vpcId}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {sg.exposedPorts.map((p, i) => (
                        <span key={i} className={`inline-block px-2 py-0.5 rounded text-xs font-mono ${
                          p.riskLevel === 'HIGH' ? 'bg-red-100 text-red-800' :
                          p.riskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {p.port}/{p.protocol}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-bold text-sm ${
                      sg.riskLevel === 'HIGH' ? 'text-red-600' :
                      sg.riskLevel === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600'
                    }`}>{sg.riskLevel}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 max-w-xs">{sg.recommendation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Trusted Advisor findings */}
      {security.trustedAdvisorFindings.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Trusted Advisor — Hallazgos de Seguridad</h4>
          <div className="space-y-2">
            {security.trustedAdvisorFindings.map((f, i) => (
              <div key={i} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                <span className={`font-medium ${f.status === 'error' ? 'text-red-700' : 'text-yellow-700'}`}>
                  [{f.status.toUpperCase()}]
                </span>{' '}
                <span className="font-medium">{f.checkName}</span>
                <p className="text-gray-600 text-xs mt-1">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Security Hub findings */}
      {security.securityHubFindings.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Security Hub — Hallazgos Críticos/Altos</h4>
          <div className="space-y-2">
            {security.securityHubFindings.slice(0, 5).map((f, i) => (
              <div key={i} className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                <span className={`font-medium ${f.severity === 'CRITICAL' ? 'text-red-700' : 'text-orange-700'}`}>
                  [{f.severity}]
                </span>{' '}
                <span className="font-medium">{f.title}</span>
                <p className="text-gray-600 text-xs mt-1">{f.recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── S3 Section ───────────────────────────────────────────────────────────────

function S3Section({ s3 }: { s3: S3Analysis }) {
  return (
    <Card>
      <CardHeader title={`Optimización S3 (${s3.buckets.length} buckets · ${s3.totalSizeGB.toFixed(1)} GB · $${formatCurrency(s3.totalEstimatedMonthlyCost)}/mes)`} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="p-3 bg-blue-50 rounded-lg text-center">
          <p className="text-xl font-bold text-blue-700">{s3.bucketsWithoutLifecycle}</p>
          <p className="text-xs text-blue-600">Sin lifecycle policy</p>
        </div>
        <div className="p-3 bg-red-50 rounded-lg text-center">
          <p className="text-xl font-bold text-red-700">{s3.publicBuckets}</p>
          <p className="text-xs text-red-600">Buckets públicos</p>
        </div>
        <div className="p-3 bg-green-50 rounded-lg text-center">
          <p className="text-xl font-bold text-green-700">${formatCurrency(s3.totalPotentialSavings)}</p>
          <p className="text-xs text-green-600">Ahorro potencial/mes</p>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg text-center">
          <p className="text-xl font-bold text-gray-700">${formatCurrency(s3.totalEstimatedMonthlyCost)}</p>
          <p className="text-xs text-gray-600">Costo estimado/mes</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-honne-light">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Bucket</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Tamaño</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Costo/mes</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Lifecycle</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Público</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Ahorro pot.</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Recomendaciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {s3.buckets.filter(b => b.sizeGB > 0 || b.recommendations.length > 0).map((b) => (
              <tr key={b.bucketName} className="hover:bg-honne-light/50">
                <td className="px-4 py-3">
                  <p className="text-sm font-medium text-gray-900">{b.bucketName}</p>
                  <p className="text-xs text-gray-500">{b.region}</p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{b.sizeGB.toFixed(2)} GB</td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">${formatCurrency(b.estimatedMonthlyCost)}</td>
                <td className="px-4 py-3 text-sm">
                  {b.hasLifecyclePolicy ? <span className="text-green-600">✅</span> : <span className="text-red-500">❌</span>}
                </td>
                <td className="px-4 py-3 text-sm">
                  {b.isPubliclyAccessible ? <span className="text-red-600 font-bold">⚠️ Sí</span> : <span className="text-green-600">No</span>}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-green-600">
                  {b.potentialMonthlySavings > 0 ? `$${formatCurrency(b.potentialMonthlySavings)}` : '—'}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600 max-w-xs">
                  {b.recommendations.length > 0 ? b.recommendations[0] : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── RDS Section ──────────────────────────────────────────────────────────────

function RDSSection({ rdsRecs }: { rdsRecs: RDSRightsizingRecommendation[] }) {
  if (rdsRecs.length === 0) return null;
  return (
    <Card>
      <CardHeader title={`RDS — Rightsizing y Actualizaciones (${rdsRecs.length} instancias)`} />
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-honne-light">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Instancia</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Motor</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Clase actual</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">CPU avg</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Memoria libre</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Actualización</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Recomendación</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rdsRecs.map((r) => (
              <tr key={r.dbInstanceIdentifier} className="hover:bg-honne-light/50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.dbInstanceIdentifier}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {r.engine} {r.engineVersion}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {r.dbInstanceClass}
                  {r.recommendedClass && (
                    <div className="text-xs text-green-600">→ {r.recommendedClass}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{r.avgCpuUtilization.toFixed(1)}%</td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {r.avgFreeableMemoryGB !== undefined ? `${r.avgFreeableMemoryGB.toFixed(2)} GB` : '—'}
                </td>
                <td className="px-4 py-3 text-sm">
                  {r.engineUpdateAvailable ? (
                    <span className={`font-medium ${
                      r.engineUpdateUrgency === 'HIGH' ? 'text-red-600' :
                      r.engineUpdateUrgency === 'MEDIUM' ? 'text-yellow-600' : 'text-blue-600'
                    }`}>
                      {r.latestEngineVersion} disponible
                    </span>
                  ) : (
                    <span className="text-green-600 text-xs">Al día</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600 max-w-xs">{r.recommendation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── PDF Download Button ──────────────────────────────────────────────────────

function PdfDownloadButton({ reportUrl }: { reportUrl: string }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(reportUrl);
      const htmlContent = await response.text();
      
      // Download as self-contained HTML file (preserves all styles perfectly)
      const blob = new Blob([htmlContent], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'reporte-optimizacion-costos-aws.html';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading report', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-sm border-2 transition-all hover:shadow-md disabled:opacity-50"
      style={{ borderColor: '#1B2B6B', color: '#1B2B6B' }}
    >
      <CloudArrowDownIcon className={`h-5 w-5 mr-2 ${downloading ? 'animate-bounce' : ''}`} />
      {downloading ? 'Descargando...' : 'Descargar Reporte'}
    </button>
  );
}

// ─── Role Config Summary (panel fijo de estado de roles) ──────────────────────

function RoleConfigSummary({
  clients,
  onSelectClient,
}: {
  clients: Client[];
  onSelectClient: (client: Client) => void;
}) {
  if (!clients || clients.length === 0) return null;

  // Clientes que requieren atención: tienen alguna cuenta sin rol, o no tienen cuentas.
  const needsAttention = clients
    .map((c) => ({ client: c, missing: countAccountsMissingRole(c), total: resolveClientAccounts(c).length }))
    .filter((x) => x.total === 0 || x.missing > 0);

  const totalMissingAccounts = needsAttention.reduce((sum, x) => sum + (x.total === 0 ? 0 : x.missing), 0);
  const clientsNoAccounts = needsAttention.filter((x) => x.total === 0).length;
  const clientsOk = clients.length - needsAttention.length;
  const allConfigured = needsAttention.length === 0;

  // Construir un detalle legible que no muestre "0 cuentas sin rol" cuando el problema
  // real es que los clientes no tienen cuentas configuradas.
  const detailParts: string[] = [];
  if (clientsNoAccounts > 0) {
    detailParts.push(`${clientsNoAccounts} cliente(s) sin cuentas`);
  }
  if (totalMissingAccounts > 0) {
    detailParts.push(`${totalMissingAccounts} cuenta(s) sin rol`);
  }
  const attentionDetail =
    `${needsAttention.length} cliente(s) requieren atención` +
    (detailParts.length > 0 ? ` · ${detailParts.join(' · ')}` : '');

  return (
    <div className="mb-6">
      <div
        className="rounded-2xl border p-4 flex flex-col lg:flex-row lg:items-center gap-4"
        style={{
          background: allConfigured
            ? 'linear-gradient(135deg, #f0fdf4, #ecfdf5)'
            : 'linear-gradient(135deg, #fffbeb, #fef3c7)',
          borderColor: allConfigured ? '#bbf7d0' : '#fde68a',
        }}
      >
        {/* Métricas */}
        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            {allConfigured ? (
              <CheckCircleIcon className="h-9 w-9 text-green-600 flex-shrink-0" />
            ) : (
              <ExclamationTriangleIcon className="h-9 w-9 text-amber-600 flex-shrink-0" />
            )}
            <div>
              <p className="text-sm font-semibold" style={{ color: allConfigured ? '#15803d' : '#b45309' }}>
                {allConfigured ? 'Configuración de roles completa' : 'Cuentas pendientes de configurar'}
              </p>
              <p className="text-xs text-gray-500">
                {allConfigured
                  ? `${clients.length} cliente(s) con roles configurados`
                  : attentionDetail}
              </p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-4 pl-4 border-l border-black/5">
            <div className="text-center">
              <p className="text-xl font-bold text-green-600">{clientsOk}</p>
              <p className="text-xs text-gray-500">OK</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-amber-600">{needsAttention.length}</p>
              <p className="text-xs text-gray-500">Pendientes</p>
            </div>
          </div>
        </div>

        {/* Lista clickeable de clientes pendientes */}
        {!allConfigured && (
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap gap-1.5">
              {needsAttention.slice(0, 12).map(({ client, missing, total }) => (
                <button
                  key={client.id}
                  onClick={() => onSelectClient(client)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-amber-200 text-amber-800 hover:border-amber-400 hover:shadow-sm transition-all"
                  title={total === 0 ? 'Sin cuentas configuradas' : `${missing} de ${total} cuenta(s) sin rol`}
                >
                  <span className="truncate max-w-[160px]">{client.name}</span>
                  <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">
                    {total === 0 ? 'sin cuentas' : `${missing} sin rol`}
                  </span>
                </button>
              ))}
              {needsAttention.length > 12 && (
                <span className="inline-flex items-center text-xs text-amber-700 px-2 py-1">
                  +{needsAttention.length - 12} más
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export function DashboardPage({ onNavigate }: DashboardPageProps) {
  const { clients, loading: clientsLoading, createClient, updateClient, deleteClient } = useClients();
  const {
    status,
    results,
    strategies,
    roleArn: assessmentRoleArn,
    loading: assessmentLoading,
    error: assessmentError,
    startAssessment,
    reset,
  } = useAssessment();

  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [configClient, setConfigClient] = useState<Client | null>(null);
  const [overrideRoleArn, setOverrideRoleArn] = useState('');
  const [arnError, setArnError] = useState('');

  const getAccountIdFromArn = (arn?: string | null): string | null => {
    if (!arn) return null;
    const parts = arn.split(':');
    return parts.length >= 5 ? parts[4] : null;
  };

  const accountId = getAccountIdFromArn(assessmentRoleArn);

  const handleDownloadTemplate = () => {
    const blob = new Blob([CLOUDFORMATION_TEMPLATE], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'honne-automated-reports-role.yaml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadMemberTemplate = () => {
    const blob = new Blob([CLOUDFORMATION_TEMPLATE_MEMBER], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'honne-automated-reports-role-member.yaml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) return;
    await createClient({ name: newClientName.trim() });
    setNewClientName('');
    setShowNewClient(false);
  };

  const handleSaveClientConfig = async (updates: UpdateClientInput) => {
    if (!configClient) return;
    const updated = await updateClient(configClient.id, updates);
    if (selectedClient?.id === configClient.id) setSelectedClient(updated);
    setConfigClient(null);
  };

  // Resolve effective accounts for a client (multi-account or legacy single)
  const resolveAccounts = (client: Client): AccountConfig[] => {
    if (client.accounts && client.accounts.length > 0) return client.accounts;
    if (client.roleArn) return [{ label: 'Principal', roleArn: client.roleArn, region: client.region || 'us-east-1' }];
    return [];
  };

  const handleStartAssessment = async () => {
    if (!selectedClient) return;
    const accounts = resolveAccounts(selectedClient);
    const hasAccounts = accounts.length > 0;

    if (!hasAccounts && !overrideRoleArn) {
      setArnError('Este cliente no tiene cuentas AWS configuradas. Ingresa un ARN o configura las cuentas en el cliente.');
      return;
    }
    if (overrideRoleArn && !/^arn:aws:iam::\d{12}:role\/[\w+=,.@-]+$/.test(overrideRoleArn)) {
      setArnError('Formato de ARN inválido');
      return;
    }
    setArnError('');

    if (overrideRoleArn) {
      // Legacy override: single account
      await startAssessment({ clientId: selectedClient.id, roleArn: overrideRoleArn });
    } else {
      // Use stored accounts (multi or single)
      await startAssessment({ clientId: selectedClient.id, accounts });
    }
  };

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [filterAccount, setFilterAccount] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [clientSearch, setClientSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['Sector Privado', 'Sector Educativo', 'Sin grupo']));
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [groupManagerSelected, setGroupManagerSelected] = useState('');

  const handleNewAssessment = () => {
    reset();
    setSelectedClient(null);
    setOverrideRoleArn('');
    setSelectedCategory(null);
    setFilterAccount('all');
    setFilterPriority('all');
  };

  // Client card component for grouped list
  const ClientCard = ({ client }: { client: Client }) => (
    <div
      className="p-3 rounded-xl border-2 cursor-pointer transition-all duration-200"
      style={selectedClient?.id === client.id
        ? { borderColor: '#E05A1C', background: 'linear-gradient(135deg, #fff8f5, #fff3ee)', boxShadow: '0 4px 12px rgba(224,90,28,0.15)' }
        : { borderColor: '#e5e7eb', background: 'white' }
      }
      onClick={() => setSelectedClient(client)}
      onMouseEnter={e => { if (selectedClient?.id !== client.id) (e.currentTarget as HTMLDivElement).style.borderColor = '#5B8DB8'; }}
      onMouseLeave={e => { if (selectedClient?.id !== client.id) (e.currentTarget as HTMLDivElement).style.borderColor = '#e5e7eb'; }}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: selectedClient?.id === client.id ? '#E05A1C' : '#1B2B6B' }}>
            <span className="text-white text-xs font-bold">{client.name.charAt(0).toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: '#1B2B6B' }}>{client.name}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-400">{client.assessmentCount} assessments</span>
              {resolveAccounts(client).length > 1 && (
                <span className="text-xs text-green-600">{resolveAccounts(client).length} cuentas</span>
              )}
              {(() => {
                const missing = countAccountsMissingRole(client);
                const total = resolveAccounts(client).length;
                if (total === 0) {
                  return (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" title="Este cliente no tiene cuentas configuradas">
                      <ExclamationTriangleIcon className="h-3 w-3" />
                      Sin cuentas
                    </span>
                  );
                }
                if (missing > 0) {
                  return (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" title={`${missing} de ${total} cuenta(s) sin rol IAM configurado`}>
                      <ExclamationTriangleIcon className="h-3 w-3" />
                      {missing} sin rol
                    </span>
                  );
                }
                return (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700" title="Todas las cuentas tienen rol configurado">
                    <CheckCircleIcon className="h-3 w-3" />
                    Configurado
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
        <div className="flex gap-1 ml-2 flex-shrink-0">
          <button onClick={(e) => { e.stopPropagation(); setConfigClient(client); }} className="p-1 rounded text-gray-400 hover:text-honne-navy hover:bg-gray-100" title="Configurar">
            <Cog6ToothIcon className="h-3.5 w-3.5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); deleteClient(client.id); }} className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" title="Eliminar">
            <span className="text-sm font-bold leading-none">×</span>
          </button>
        </div>
      </div>
    </div>
  );

  // ── Results view ──────────────────────────────────────────────────────────

  if (results && status?.status === 'COMPLETED') {
    const security = results.securityAnalysis;
    const s3 = results.s3Analysis;
    const rdsRecs = results.rdsRightsizingRecommendations || [];

    // SVG icons per category
    const CategoryIcon = ({ cat, className }: { cat: string; className?: string }) => {
      const cls = className || 'w-7 h-7';
      if (cat === 'IDLE_RESOURCES') return (
        // Power-off server
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="5" rx="1"/>
          <rect x="2" y="10" width="20" height="5" rx="1"/>
          <line x1="12" y1="17" x2="12" y2="21"/>
          <line x1="9" y1="21" x2="15" y2="21"/>
          <line x1="2" y1="2" x2="22" y2="22" strokeWidth="2"/>
        </svg>
      );
      if (cat === 'RIGHTSIZING') return (
        // Resize arrows
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 3 21 3 21 9"/>
          <polyline points="9 21 3 21 3 15"/>
          <line x1="21" y1="3" x2="14" y2="10"/>
          <line x1="3" y1="21" x2="10" y2="14"/>
        </svg>
      );
      if (cat === 'SAVINGS_PLANS') return (
        // Price tag
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
          <line x1="7" y1="7" x2="7.01" y2="7" strokeWidth="2.5"/>
        </svg>
      );
      if (cat === 'S3_OPTIMIZATION') return (
        // S3 bucket / cylinder
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <ellipse cx="12" cy="5" rx="9" ry="3"/>
          <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
          <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/>
        </svg>
      );
      // SECURITY — shield with lock
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <rect x="9" y="11" width="6" height="5" rx="1"/>
          <path d="M10 11V9a2 2 0 014 0v2"/>
        </svg>
      );
    };

    const categories = [
      { key: 'IDLE_RESOURCES',  label: 'Recursos Ociosos', textColor: 'text-red-600',    bgColor: 'bg-red-50',    borderColor: 'border-red-200',    activeBg: 'bg-red-100' },
      { key: 'RIGHTSIZING',     label: 'Rightsizing',      textColor: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', activeBg: 'bg-yellow-100' },
      { key: 'SAVINGS_PLANS',   label: 'Savings Plans',    textColor: 'text-blue-600',   bgColor: 'bg-blue-50',   borderColor: 'border-blue-200',   activeBg: 'bg-blue-100' },
      { key: 'S3_OPTIMIZATION', label: 'S3 Optimization',  textColor: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-200', activeBg: 'bg-purple-100' },
      { key: 'SECURITY',        label: 'Seguridad',        textColor: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200', activeBg: 'bg-orange-100' },
    ];

    const filteredRecs = selectedCategory
      ? results.recommendations.filter(r => {
          if (r.category !== selectedCategory) return false;
          if (filterAccount !== 'all' && r.accountLabel !== filterAccount) return false;
          if (filterPriority !== 'all' && r.priority !== filterPriority) return false;
          return true;
        })
      : [];

    return (
      <Layout onNavigate={onNavigate}>
        <div className="flex gap-6">
          <div className="flex-1 space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-honne-navy">Resultados del Assessment</h2>
                {status.accounts && status.accounts.length > 1 ? (
                  <p className="text-sm text-gray-600 mt-1">
                    {(() => {
                      const seen = new Set<string>();
                      const unique = status.accounts.filter((a: AccountConfig) => {
                        const id = a.roleArn?.match(/:(\d{12}):/)?.[1] || a.label;
                        if (a.label?.toUpperCase().includes('MPA') || a.isMpaAccount) return false;
                        if (seen.has(id)) return false;
                        seen.add(id);
                        return true;
                      });
                      return `${unique.length} cuentas analizadas: ${unique.map((a: AccountConfig) => a.label).join(' · ')}`;
                    })()}
                  </p>
                ) : accountId ? (
                  <p className="text-sm text-gray-600 mt-1">AWS Account: {accountId}</p>
                ) : null}
              </div>
              <div className="flex gap-3">
                {status.reportUrl && (
                  <a
                    href={status.reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-sm bg-gradient-accent text-white hover:shadow-lg transition-all"
                  >
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    Ver Reporte
                  </a>
                )}
                {status.reportUrl && (
                  <PdfDownloadButton reportUrl={status.reportUrl} />
                )}
                <Button variant="secondary" onClick={() => onNavigate?.('/reports')}>
                  Historial
                </Button>
                <Button variant="secondary" onClick={handleNewAssessment}>
                  Nuevo Assessment
                </Button>
              </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <div className="text-center">
                  <p className="text-sm text-green-700 font-medium">Ahorro Mensual Potencial</p>
                  <p className="text-2xl font-bold text-green-600 mt-2 break-all">${formatCurrency(results.savingsSummary.totalMonthlySavings)}</p>
                  {selectedClient?.marginPercentage ? (
                    <p className="text-xs text-green-700 mt-1 font-semibold">
                      Con margen: ${formatCurrency(results.savingsSummary.totalMonthlySavings * (1 + selectedClient.marginPercentage / 100))}
                    </p>
                  ) : null}
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-gray-600 font-medium">Costo Mes Actual</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2 break-all">${formatCurrency(results.costSummary.totalCost)}</p>
                  {selectedClient?.marginPercentage ? (
                    <p className="text-xs text-accent-600 mt-1 font-semibold">
                      Facturación: ${formatCurrency(results.costSummary.totalCost * (1 + selectedClient.marginPercentage / 100))}
                      <span className="text-gray-400 ml-1">(+{selectedClient.marginPercentage}%)</span>
                    </p>
                  ) : null}
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-gray-600 font-medium">Mes Anterior</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2 break-all">${formatCurrency(results.costSummary.previousMonthCost)}</p>
                  {selectedClient?.marginPercentage ? (
                    <p className="text-xs text-accent-600 mt-1 font-semibold">
                      Facturación: ${formatCurrency(results.costSummary.previousMonthCost * (1 + selectedClient.marginPercentage / 100))}
                    </p>
                  ) : null}
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-gray-600 font-medium">Recomendaciones</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{results.recommendations.length}</p>
                </div>
              </Card>
            </div>

            {/* Category savings — seleccionables */}
            <Card>
              <CardHeader title="Ahorro por Categoría" />
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {categories.map(({ key, label, textColor, bgColor, borderColor, activeBg }) => {
                  const isSelected = selectedCategory === key;
                  const count = results.recommendations.filter(r => r.category === key).length;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedCategory(isSelected ? null : key);
                        setFilterAccount('all');
                        setFilterPriority('all');
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                        isSelected
                          ? `${activeBg} ${borderColor} shadow-md ring-2 ring-offset-1 ring-current`
                          : `${bgColor} ${borderColor} hover:shadow-sm hover:scale-[1.02]`
                      }`}
                    >
                      <div className={`mb-2 ${textColor}`}>
                        <CategoryIcon cat={key} />
                      </div>
                      <p className={`text-sm font-semibold ${textColor}`}>{label}</p>
                      {key === 'SECURITY' ? (
                        <p className={`text-xl font-bold ${textColor} mt-1`}>
                          {count} <span className="text-sm font-normal">hallazgos</span>
                        </p>
                      ) : (
                        <p className={`text-xl font-bold ${textColor} mt-1`}>
                          ${formatCurrency(results.savingsSummary.byCategory[key] || 0)}
                        </p>
                      )}
                      <p className={`text-xs mt-1 ${textColor} opacity-70`}>
                        {count} hallazgos
                        {isSelected ? ' · Haz clic para cerrar' : ' · Haz clic para ver'}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Hallazgos de la categoría seleccionada */}
              {selectedCategory && filteredRecs.length >= 0 && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`${categories.find(c => c.key === selectedCategory)?.textColor}`}>
                      <CategoryIcon cat={selectedCategory} className="w-5 h-5" />
                    </div>
                    <h4 className="text-sm font-semibold text-gray-700">
                      {categories.find(c => c.key === selectedCategory)?.label} ({filteredRecs.length})
                    </h4>
                  </div>

                  {/* Filters */}
                  <div className="flex flex-wrap items-center gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
                    {/* Account filter */}
                    {results.recommendations.some(r => r.accountLabel) && (
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-500">Cuenta:</label>
                        <select
                          value={filterAccount}
                          onChange={e => setFilterAccount(e.target.value)}
                          className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white focus:ring-2 focus:ring-accent-500"
                        >
                          <option value="all">Todas</option>
                          {Array.from(new Set(results.recommendations.filter(r => r.category === selectedCategory && r.accountLabel).map(r => r.accountLabel!))).map(label => (
                            <option key={label} value={label}>{label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {/* Priority filter */}
                    <div className="flex items-center gap-1">
                      <label className="text-xs font-medium text-gray-500 mr-1">Prioridad:</label>
                      {['all', 'HIGH', 'MEDIUM', 'LOW'].map(p => (
                        <button
                          key={p}
                          onClick={() => setFilterPriority(p)}
                          className={`text-xs px-2 py-1 rounded-md font-medium transition-colors ${
                            filterPriority === p
                              ? p === 'HIGH' ? 'bg-red-100 text-red-700 ring-1 ring-red-300'
                                : p === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700 ring-1 ring-yellow-300'
                                : p === 'LOW' ? 'bg-green-100 text-green-700 ring-1 ring-green-300'
                                : 'bg-honne-navy text-white'
                              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          {p === 'all' ? 'Todas' : p === 'HIGH' ? 'Alta' : p === 'MEDIUM' ? 'Media' : 'Baja'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-honne-light">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-honne-navy uppercase">Recurso</th>
                          {filteredRecs.some(r => r.accountLabel) && (
                            <th className="px-4 py-2 text-left text-xs font-medium text-honne-navy uppercase">Cuenta</th>
                          )}
                          {filteredRecs.some(r => r.details?._region) && (
                            <th className="px-4 py-2 text-left text-xs font-medium text-honne-navy uppercase">Región</th>
                          )}
                          <th className="px-4 py-2 text-left text-xs font-medium text-honne-navy uppercase">Descripción</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-honne-navy uppercase">Ahorro/mes</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-honne-navy uppercase">Prioridad</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredRecs.map((rec) => (
                          <tr key={rec.id} className="hover:bg-honne-light/50">
                            <td className="px-4 py-2 text-xs font-medium text-gray-900">
                              {rec.resourceName || rec.resourceId?.substring(0, 25)}
                              <div className="text-gray-400 font-mono">{rec.resourceId?.substring(0, 20)}</div>
                            </td>
                            {filteredRecs.some(r => r.accountLabel) && (
                              <td className="px-4 py-2 whitespace-nowrap">
                                {rec.accountLabel ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                    {rec.accountLabel}
                                  </span>
                                ) : '—'}
                              </td>
                            )}
                            {filteredRecs.some(r => r.details?._region) && (
                              <td className="px-4 py-2 text-xs text-gray-500">
                                {(rec.details?._region as string) || '—'}
                              </td>
                            )}
                            <td className="px-4 py-2 text-xs text-gray-600 max-w-sm">{rec.description}</td>
                            <td className="px-4 py-2 text-xs font-medium text-accent-600 whitespace-nowrap">
                              {rec.estimatedMonthlySavings > 0 ? `$${formatCurrency(rec.estimatedMonthlySavings)}` : '—'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span className={`text-xs font-bold ${
                                rec.priority === 'HIGH' ? 'text-red-600' :
                                rec.priority === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600'
                              }`}>{rec.priority}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {selectedCategory && filteredRecs.length === 0 && (
                <div className="text-center text-sm text-gray-500 py-4">
                  No hay hallazgos para los filtros seleccionados
                </div>
              )}
            </Card>

            {/* Security, S3, RDS sections removed — now only shown via category click */}

          </div>

          {/* Account health sidebar — replaces strategies */}
          {status.accounts && status.accounts.length > 1 && (
            <div className="w-80 flex-shrink-0">
              <Card className="sticky top-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-honne-light flex items-center justify-center">
                    <svg className="w-5 h-5 text-honne-navy" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  </div>
                  <h3 className="text-sm font-bold text-honne-navy">Salud por Cuenta</h3>
                </div>
                <div className="space-y-2">
                  {(() => {
                    // Deduplicate accounts by accountId (from the header)
                    const seen = new Set<string>();
                    const uniqueAccounts = (status.accounts || []).filter(a => {
                      const id = a.roleArn?.match(/:(\d{12}):/)?.[1] || a.label;
                      if (id.includes('MPA') || a.label?.toUpperCase().includes('MPA')) return false;
                      if (seen.has(id)) return false;
                      seen.add(id);
                      return true;
                    });

                    return uniqueAccounts.filter(acc => {
                      // Hide accounts with 0 hallazgos and $0 savings
                      const accId = acc.roleArn?.match(/:(\d{12}):/)?.[1] || '';
                      const accRecs = results.recommendations.filter(r =>
                        r.accountLabel === acc.label ||
                        r.accountLabel?.includes(accId) ||
                        (r.details as Record<string, unknown>)?.accountId === accId
                      );
                      return accRecs.length > 0;
                    }).map((acc, i) => {
                      // Calculate health based on savings % and security findings
                      // Filter by accountLabel OR by matching accountId in the recommendation details
                      const accId = acc.roleArn?.match(/:(\d{12}):/)?.[1] || '';
                      const accRecs = results.recommendations.filter(r =>
                        r.accountLabel === acc.label ||
                        r.accountLabel?.includes(accId) ||
                        (r.details as Record<string, unknown>)?.accountId === accId
                      );
                      const accSavings = accRecs.reduce((s, r) => s + r.estimatedMonthlySavings, 0);
                      const secHigh = accRecs.filter(r => r.category === 'SECURITY' && r.priority === 'HIGH').length;
                      const totalCost = results.costSummary.totalCost / Math.max(uniqueAccounts.length, 1);

                      const savingsPct = totalCost > 0 ? (accSavings / totalCost) * 100 : 0;
                      let color = 'bg-green-500';
                      let label = 'Saludable';
                      if (savingsPct > 20 || secHigh > 5) { color = 'bg-red-500'; label = 'Crítico'; }
                      else if (savingsPct > 10 || secHigh > 0) { color = 'bg-yellow-500'; label = 'Atención'; }

                      return (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                          <div className={`w-3 h-3 rounded-full ${color} flex-shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-800 truncate">{acc.label}</p>
                            <p className="text-xs text-gray-500">{accRecs.length} hallazgos · ${formatCurrency(accSavings)}/mes</p>
                          </div>
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                            color === 'bg-red-500' ? 'bg-red-100 text-red-700' :
                            color === 'bg-yellow-500' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>{label}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </Card>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ── Progress view ─────────────────────────────────────────────────────────

  if (status && (status.status === 'RUNNING' || status.status === 'PENDING')) {
    return (
      <Layout onNavigate={onNavigate}>
        <Card className="max-w-2xl mx-auto text-center py-12">
          <Spinner size="lg" className="mx-auto mb-6" />
          <h2 className="text-xl font-semibold text-honne-navy mb-2">Assessment en Progreso</h2>
          <p className="text-gray-600 mb-6">{status.currentStep.replace(/_/g, ' ')}</p>
          <div className="w-full bg-gray-200 rounded-full h-4 mb-2 overflow-hidden">
            <div
              className="bg-gradient-accent h-4 rounded-full transition-all duration-500"
              style={{ width: `${status.progress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500">{status.progress}% completado</p>
        </Card>
      </Layout>
    );
  }

  // ── Error view ────────────────────────────────────────────────────────────

  if (status && status.status === 'FAILED') {
    return (
      <Layout onNavigate={onNavigate}>
        <Card className="max-w-2xl mx-auto py-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-red-600 mb-2">Assessment Fallido</h2>
          <p className="text-sm text-gray-500 mb-4">Paso: {status.currentStep.replace(/_/g, ' ')}</p>
          {status.error?.message && (
            <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
              <p className="text-xs font-mono text-gray-600 break-all">{status.error.message}</p>
            </div>
          )}
          <Button onClick={handleNewAssessment}>Intentar Nuevamente</Button>
        </Card>
      </Layout>
    );
  }

  // ── Main form view ────────────────────────────────────────────────────────

  return (
    <Layout onNavigate={onNavigate}>
      {configClient && (
        <ClientConfigModal
          client={configClient}
          onSave={handleSaveClientConfig}
          onClose={() => setConfigClient(null)}
        />
      )}

      {/* Group Manager Modal */}
      {showGroupManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-bold text-honne-navy">Gestionar Grupos</h3>
              <p className="text-sm text-gray-500 mt-1">Asigna clientes a grupos para organizarlos</p>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {(() => {
                const existingGroups = Array.from(new Set(clients.map(c => c.group).filter(Boolean))) as string[];
                const [selectedGroup, setSelectedGroupLocal] = [
                  groupManagerSelected,
                  setGroupManagerSelected,
                ];

                return (
                  <div className="space-y-4">
                    {/* Group selector / creator */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Selecciona o crea un grupo</label>
                      <div className="flex gap-2">
                        <input
                          value={selectedGroup}
                          onChange={e => setSelectedGroupLocal(e.target.value)}
                          placeholder="Nombre del grupo (ej: Sector Privado)"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                          list="existing-groups"
                        />
                        <datalist id="existing-groups">
                          {existingGroups.map(g => <option key={g} value={g} />)}
                        </datalist>
                      </div>
                      {existingGroups.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {existingGroups.map(g => (
                            <button
                              key={g}
                              onClick={() => setSelectedGroupLocal(g)}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                selectedGroup === g
                                  ? 'bg-honne-navy text-white border-honne-navy'
                                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-honne-navy'
                              }`}
                            >
                              {g}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Client checkboxes */}
                    {selectedGroup && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-2">
                          Clientes en "{selectedGroup}" — marca los que pertenecen a este grupo:
                        </p>
                        <div className="space-y-1 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2">
                          {clients.map(c => (
                            <label key={c.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={c.group === selectedGroup}
                                onChange={async () => {
                                  const newGroup = c.group === selectedGroup ? undefined : selectedGroup;
                                  await updateClient(c.id, { group: newGroup || '' });
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-accent-500 focus:ring-accent-500"
                              />
                              <span className="text-sm text-gray-800">{c.name}</span>
                              {c.group && c.group !== selectedGroup && (
                                <span className="text-xs text-gray-400 ml-auto">{c.group}</span>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end">
              <Button onClick={() => setShowGroupManager(false)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#1B2B6B' }}>Dashboard</h2>
          <p className="text-sm text-gray-500 mt-0.5">Gestiona clientes y ejecuta assessments de optimización</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white" style={{ background: 'linear-gradient(135deg, #1B2B6B, #5B8DB8)' }}>
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          Sistema activo
        </div>
      </div>

      {/* Panel de resumen de configuración de roles (fijo, siempre visible) */}
      <RoleConfigSummary
        clients={clients}
        onSelectClient={(client) => {
          setSelectedClient(client);
          if (client.group) {
            setExpandedGroups((prev) => new Set(prev).add(client.group!));
          }
        }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Clients */}
        <Card>
          <CardHeader
            title="Clientes"
            action={
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setShowGroupManager(true)}>
                  Grupos
                </Button>
                <Button size="sm" onClick={() => setShowNewClient(true)}>
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Nuevo
                </Button>
              </div>
            }
          />

          {showNewClient && (
            <div className="mb-4 p-4 rounded-xl border" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
              <Input
                label="Nombre del cliente"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder="Ej: Empresa ABC"
              />
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={handleCreateClient}>Guardar</Button>
                <Button size="sm" variant="outline" onClick={() => setShowNewClient(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          {clientsLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : clients.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #eef2f7, #dde4f0)' }}>
                <span className="text-3xl">🏢</span>
              </div>
              <p className="text-gray-500 font-medium">No hay clientes registrados</p>
              <p className="text-sm text-gray-400 mt-1">Crea el primer cliente para comenzar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Search */}
              <input
                type="text"
                placeholder="Buscar cliente..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                onChange={e => {
                  const q = e.target.value.toLowerCase();
                  setClientSearch(q);
                }}
              />

              {/* Grouped clients */}
              {(() => {
                const filtered = clients.filter(c => c.name.toLowerCase().includes(clientSearch));
                const groups = new Map<string, typeof clients>();
                filtered.forEach(c => {
                  const g = c.group || 'Sin grupo';
                  if (!groups.has(g)) groups.set(g, []);
                  groups.get(g)!.push(c);
                });

                // If only one group or no groups, show flat list
                if (groups.size <= 1 && !filtered.some(c => c.group)) {
                  return filtered.map(client => (
                    <ClientCard key={client.id} client={client} />
                  ));
                }

                return Array.from(groups.entries()).map(([groupName, groupClients]) => (
                  <div key={groupName}>
                    <button
                      onClick={() => setExpandedGroups(prev => {
                        const next = new Set(prev);
                        next.has(groupName) ? next.delete(groupName) : next.add(groupName);
                        return next;
                      })}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-400">{expandedGroups.has(groupName) ? '▼' : '▶'}</span>
                        <span className="text-sm font-semibold text-honne-navy">{groupName}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{groupClients.length}</span>
                      </div>
                    </button>
                    {expandedGroups.has(groupName) && (
                      <div className="space-y-1.5 ml-4 mt-1">
                        {groupClients.map(client => (
                          <ClientCard key={client.id} client={client} />
                        ))}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
          )}
        </Card>

        {/* Assessment */}
        <Card>
          <CardHeader title="Iniciar Assessment" />

          {/* Download template */}
          <div className="mb-6 p-4 rounded-xl border" style={{ background: 'linear-gradient(135deg, #f8fafc, #eef2f7)', borderColor: '#dde4f0' }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #1B2B6B, #5B8DB8)' }}>
                <CloudArrowDownIcon className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold mb-1" style={{ color: '#1B2B6B' }}>¿El cliente no tiene el rol configurado?</p>
                <p className="text-sm text-gray-600 mb-3">
                  Descarga el template de CloudFormation y compártelo con tu cliente.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={handleDownloadTemplate} variant="secondary">
                    <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                    Cuenta individual / MPA
                  </Button>
                  <Button size="sm" onClick={handleDownloadMemberTemplate} variant="secondary">
                    <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                    Cuentas miembro (StackSets)
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Para MPA: despliega el primer template en la cuenta raíz y el segundo en todas las cuentas miembro via CloudFormation StackSets.
                </p>
              </div>
            </div>
          </div>

          {assessmentError && <Alert type="error" message={assessmentError} className="mb-4" />}

          {selectedClient ? (
            <div className="space-y-4">
              <div className="p-4 bg-honne-light rounded-xl border border-honne-blue/10">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Cliente seleccionado</p>
                    <p className="font-semibold text-honne-navy">{selectedClient.name}</p>
                    {(() => {
                      const accs = resolveAccounts(selectedClient);
                      if (accs.length > 1) return (
                        <p className="text-xs text-gray-500 mt-0.5">{accs.length} cuentas: {accs.map(a => a.label).join(', ')}</p>
                      );
                      if (accs.length === 1) return (
                        <p className="text-xs text-gray-500 mt-0.5">Región: {accs[0].region}</p>
                      );
                      return null;
                    })()}
                  </div>
                  <button
                    onClick={() => setConfigClient(selectedClient)}
                    className="text-xs text-honne-blue hover:underline flex items-center gap-1"
                  >
                    <Cog6ToothIcon className="h-3 w-3" />
                    Configurar
                  </button>
                </div>
              </div>

              {(() => {
                const accs = resolveAccounts(selectedClient);
                if (accs.length > 1) return (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 font-medium">✅ {accs.length} cuentas configuradas</p>
                    <div className="mt-2 space-y-1">
                      {accs.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-green-600">
                          <span className="font-semibold">{a.label}</span>
                          <span className="text-green-400">·</span>
                          <span className="font-mono">{a.roleArn.split('/').pop()}</span>
                          <span className="text-green-400">·</span>
                          <span>{a.region}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-green-600 mt-2">El assessment analizará todas las cuentas en paralelo.</p>
                  </div>
                );
                if (accs.length === 1) return (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">
                      ✅ Rol configurado: <span className="font-mono text-xs">{accs[0].roleArn.split('/').pop()}</span>
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      El assessment usará el rol guardado. Puedes sobreescribirlo abajo si es necesario.
                    </p>
                  </div>
                );
                return <Alert type="warning" message="Este cliente no tiene cuentas AWS configuradas. Ingresa un ARN o configura las cuentas en el cliente." />;
              })()}

              {(resolveAccounts(selectedClient).length === 0 || overrideRoleArn) && (
                <Input
                  label={resolveAccounts(selectedClient).length === 0 ? 'ARN del Rol IAM *' : 'Sobreescribir ARN del Rol (opcional)'}
                  value={overrideRoleArn}
                  onChange={(e) => { setOverrideRoleArn(e.target.value); setArnError(''); }}
                  placeholder="arn:aws:iam::123456789012:role/HonneCostOptimizationRole"
                  error={arnError}
                />
              )}

              {arnError && resolveAccounts(selectedClient).length > 0 && (
                <p className="text-sm text-red-600">{arnError}</p>
              )}

              <Button
                onClick={handleStartAssessment}
                loading={assessmentLoading}
                disabled={resolveAccounts(selectedClient).length === 0 && !overrideRoleArn}
                className="w-full"
              >
                <PlayIcon className="h-5 w-5 mr-2" />
                {resolveAccounts(selectedClient).length > 1
                  ? `Iniciar Assessment (${resolveAccounts(selectedClient).length} cuentas)`
                  : 'Iniciar Assessment'}
              </Button>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-honne-light flex items-center justify-center">
                <PlayIcon className="h-8 w-8 text-honne-blue" />
              </div>
              <p>Selecciona un cliente para iniciar un assessment</p>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
